import * as React from 'react'
import { AlertCircle, CheckCircle2, Clock3, Loader2, RefreshCw } from 'lucide-react'
import {
  useGetAuthzMeQuery,
  useListPssMirrorRunsQuery,
  useListPssMirrorsQuery,
  useUpdatePssMirrorRefreshMutation,
  type PssMirror,
  type PssMirrorRun,
  type PssMirrorRunTableStat
} from '@axilon-apps/domains-ontology-ui-state'

import { readMutationError } from '../../../components/explorer'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { cn } from '../../../lib/utils'
import { formatCompactLocalTime, formatEventWindowUTC, formatRelativeTime } from '../../../lib/time'
import { PSS_WIZARD_LIMITS } from '../wizard/pss'

const MIN_REFRESH_MINUTES = PSS_WIZARD_LIMITS.minRefreshSeconds / 60
const MAX_REFRESH_MINUTES = PSS_WIZARD_LIMITS.maxRefreshSeconds / 60

function runTone(status: string): string {
  switch (status) {
    case 'succeeded':
      return 'border-st-run/30 bg-st-run-bg text-st-run'
    case 'partial':
      return 'border-st-warn/30 bg-st-warn-bg text-st-warn'
    case 'failed':
      return 'border-st-down/30 bg-st-down-bg text-st-down'
    default:
      return 'border-st-info/30 bg-st-info-bg text-st-info'
  }
}

function runLabel(status: string): string {
  return status === 'running' ? 'Running' : status.charAt(0).toUpperCase() + status.slice(1)
}

function mirrorSummary(mirror: PssMirror): {
  icon: React.ComponentType<{ className?: string }>
  label: string
  className: string
} {
  if (mirror.lease_until && Date.parse(mirror.lease_until) > Date.now()) {
    return { icon: Loader2, label: 'Syncing', className: 'text-st-info' }
  }
  if (mirror.last_error) {
    return {
      icon: AlertCircle,
      label: 'Needs attention',
      className: 'text-st-down'
    }
  }
  if (mirror.last_success_at) {
    return { icon: CheckCircle2, label: 'Healthy', className: 'text-st-run' }
  }
  return {
    icon: Clock3,
    label: 'Waiting for first sync',
    className: 'text-st-warn'
  }
}

function journalStats(run: PssMirrorRun): PssMirrorRunTableStat[] {
  return run.tables.filter((table) => table.table === 'event_journal')
}

function ownerName(stat: PssMirrorRunTableStat): string {
  return stat.owner_name || stat.owner_id || 'Unknown owner'
}

function RunLog({ run }: { run: PssMirrorRun }) {
  const journals = journalStats(run)
  const completedAt = run.finished_at ?? run.started_at

  return (
    <div className="rounded-lg border border-line bg-bg-elev">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-3.5 py-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className={cn('border', runTone(run.status))}>
              {runLabel(run.status)}
            </Badge>
            <span className="text-xs font-medium text-ink">{run.kind} sync</span>
          </div>
          <div className="mt-1 text-11 text-ink-3">
            {formatCompactLocalTime(run.started_at, {
              fallback: run.started_at
            })}
            {run.finished_at ? ` · finished ${formatRelativeTime(run.finished_at)}` : ''}
          </div>
        </div>
        <div className="text-right text-11 text-ink-3">
          <div>{run.rows_upserted.toLocaleString()} total rows</div>
          <div>{run.calls.toLocaleString()} source calls</div>
        </div>
      </div>

      <div className="flex flex-col gap-2 px-3.5 py-3">
        {journals.length === 0 ? (
          <div className="text-xs text-ink-3">No journal windows were due for this sync.</div>
        ) : (
          journals.map((stat, index) => (
            <div
              key={`${stat.owner_id ?? stat.owner_name ?? 'owner'}-${index}`}
              className={cn(
                'rounded-md border px-3 py-2 text-xs',
                stat.error ? 'border-st-down/30 bg-st-down-bg' : 'border-line bg-bg-sunk'
              )}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-ink">{ownerName(stat)}</span>
                <span className={stat.error ? 'text-st-down' : 'text-ink-2'}>
                  {stat.rows.toLocaleString()} row{stat.rows === 1 ? '' : 's'} · {stat.windows ?? 0}{' '}
                  interval{stat.windows === 1 ? '' : 's'}
                </span>
              </div>
              {stat.window_from ? (
                <div className="mt-1 font-mono text-11 text-ink-3">
                  {formatEventWindowUTC(stat.window_from, stat.window_to)}
                </div>
              ) : null}
              {stat.error ? <div className="mt-1 text-st-down">{stat.error}</div> : null}
            </div>
          ))
        )}

        {run.error ? (
          <div className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">{run.error}</div>
        ) : null}
        {run.warnings.map((warning, index) => (
          <div
            key={`${completedAt}-${index}`}
            className="rounded-md bg-st-warn-bg px-3 py-2 text-xs text-st-warn"
          >
            {warning}
          </div>
        ))}
      </div>
    </div>
  )
}

export function PssSyncTab() {
  // PSS mirrors and their runs publish no wake-up, so both stay deliberate polls.
  const mirrorsQuery = useListPssMirrorsQuery(undefined, {
    pollingInterval: 15_000,
    refetchOnFocus: true
  })
  const authzQuery = useGetAuthzMeQuery()
  const canManage = authzQuery.data?.permissions.includes('integration/manage') === true
  const mirrors = React.useMemo(() => mirrorsQuery.data ?? [], [mirrorsQuery.data])
  const [selectedMirrorId, setSelectedMirrorId] = React.useState('')
  const selectedMirror =
    mirrors.find((mirror) => mirror.id === selectedMirrorId) ?? mirrors[0] ?? null

  React.useEffect(() => {
    if (!selectedMirrorId && mirrors[0]) setSelectedMirrorId(mirrors[0].id)
    if (selectedMirrorId && !mirrors.some((mirror) => mirror.id === selectedMirrorId)) {
      setSelectedMirrorId(mirrors[0]?.id ?? '')
    }
  }, [mirrors, selectedMirrorId])

  const runsQuery = useListPssMirrorRunsQuery(
    { mirrorId: selectedMirror?.id ?? '', limit: 50 },
    {
      skip: !selectedMirror,
      pollingInterval: 10_000,
      refetchOnFocus: true
    }
  )
  const [updateRefresh, updateState] = useUpdatePssMirrorRefreshMutation()
  const [refreshMinutes, setRefreshMinutes] = React.useState('')
  const [saveNotice, setSaveNotice] = React.useState('')

  React.useEffect(() => {
    setRefreshMinutes(
      selectedMirror ? String(Math.round(selectedMirror.refresh_interval_seconds / 60)) : ''
    )
    setSaveNotice('')
  }, [selectedMirror])

  const saveRefresh = async () => {
    if (!selectedMirror) return
    const minutes = Number(refreshMinutes)
    if (
      !Number.isInteger(minutes) ||
      minutes < MIN_REFRESH_MINUTES ||
      minutes > MAX_REFRESH_MINUTES
    ) {
      setSaveNotice(
        `Enter a whole number from ${MIN_REFRESH_MINUTES} to ${MAX_REFRESH_MINUTES} minutes.`
      )
      return
    }
    setSaveNotice('')
    try {
      await updateRefresh({
        mirrorId: selectedMirror.id,
        refreshIntervalSeconds: minutes * 60
      }).unwrap()
      setSaveNotice('Update frequency saved.')
    } catch (error) {
      setSaveNotice(readMutationError(error))
    }
  }

  if (mirrorsQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-line bg-bg-elev px-4 py-6 text-sm text-ink-3">
        <Loader2 className="size-4 animate-spin" />
        Loading PlantState Suite mirrors…
      </div>
    )
  }

  if (mirrorsQuery.error) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-st-down/30 bg-st-down-bg px-4 py-3 text-sm text-st-down"
      >
        Could not load PlantState Suite syncs: {readMutationError(mirrorsQuery.error)}
      </div>
    )
  }

  if (mirrors.length === 0) {
    return (
      <div className="rounded-lg border border-line bg-bg-elev px-4 py-6 text-sm text-ink-3">
        No PlantState Suite mirrors are configured. Start a PSS ingest to create one.
      </div>
    )
  }

  const summary = selectedMirror ? mirrorSummary(selectedMirror) : null
  const SummaryIcon = summary?.icon

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        {mirrors.map((mirror) => {
          const itemSummary = mirrorSummary(mirror)
          const ItemIcon = itemSummary.icon
          const selected = mirror.id === selectedMirror?.id
          return (
            <button
              key={mirror.id}
              type="button"
              aria-pressed={selected}
              onClick={() => setSelectedMirrorId(mirror.id)}
              className={cn(
                'min-w-52 rounded-lg border px-3 py-2 text-left transition-colors',
                selected
                  ? 'border-st-info bg-st-info-bg'
                  : 'border-line bg-bg-elev hover:bg-bg-sunk'
              )}
            >
              <div className="text-sm font-medium text-ink">{mirror.name}</div>
              <div className={cn('mt-1 flex items-center gap-1 text-11', itemSummary.className)}>
                <ItemIcon
                  className={cn('size-3', itemSummary.label === 'Syncing' && 'animate-spin')}
                />
                {itemSummary.label}
              </div>
            </button>
          )
        })}
      </div>

      {selectedMirror ? (
        <div className="rounded-lg border border-line bg-bg-elev p-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-ink">{selectedMirror.name}</h3>
                {summary && SummaryIcon ? (
                  <span className={cn('flex items-center gap-1 text-xs', summary.className)}>
                    <SummaryIcon
                      className={cn('size-3.5', summary.label === 'Syncing' && 'animate-spin')}
                    />
                    {summary.label}
                  </span>
                ) : null}
              </div>
              <div className="mt-1 truncate font-mono text-11 text-ink-3">
                {selectedMirror.service_url}
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-3">
                <span>
                  Last success:{' '}
                  {formatRelativeTime(selectedMirror.last_success_at, {
                    fallback: 'never'
                  })}
                </span>
                <span>
                  Next update:{' '}
                  {formatRelativeTime(selectedMirror.next_run_at, {
                    fallback: 'not scheduled'
                  })}
                </span>
              </div>
              {selectedMirror.last_error ? (
                <div className="mt-2 text-xs text-st-down">{selectedMirror.last_error}</div>
              ) : null}
            </div>

            <div className="flex flex-col items-end gap-1.5">
              <label htmlFor="pss-refresh-minutes" className="text-xs font-medium text-ink-2">
                Update every
              </label>
              <div className="flex items-center gap-2">
                <Input
                  id="pss-refresh-minutes"
                  type="number"
                  min={MIN_REFRESH_MINUTES}
                  max={MAX_REFRESH_MINUTES}
                  step={1}
                  className="h-8 w-24"
                  value={refreshMinutes}
                  onChange={(event) => {
                    setRefreshMinutes(event.target.value)
                    setSaveNotice('')
                  }}
                  disabled={!canManage || updateState.isLoading}
                />
                <span className="text-xs text-ink-3">minutes</span>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void saveRefresh()}
                  disabled={!canManage || updateState.isLoading}
                >
                  {updateState.isLoading ? <Loader2 className="size-3.5 animate-spin" /> : null}
                  Save
                </Button>
              </div>
              {!canManage && !authzQuery.isLoading ? (
                <span className="text-11 text-ink-3">
                  Integration management permission is required.
                </span>
              ) : null}
              {saveNotice ? (
                <span
                  role="status"
                  className={cn(
                    'text-11',
                    saveNotice === 'Update frequency saved.' ? 'text-st-run' : 'text-st-down'
                  )}
                >
                  {saveNotice}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ink">Journal update log</h3>
          <p className="mt-0.5 text-xs text-ink-3">
            Each entry shows the journal windows attempted for every data owner.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => void Promise.all([mirrorsQuery.refetch(), runsQuery.refetch()])}
          disabled={mirrorsQuery.isFetching || runsQuery.isFetching}
        >
          <RefreshCw
            className={cn(
              'size-3.5',
              (mirrorsQuery.isFetching || runsQuery.isFetching) && 'animate-spin'
            )}
          />
          Refresh
        </Button>
      </div>

      {runsQuery.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-ink-3">
          <Loader2 className="size-4 animate-spin" />
          Loading sync history…
        </div>
      ) : runsQuery.error ? (
        <div
          role="alert"
          className="rounded-lg border border-st-down/30 bg-st-down-bg px-4 py-3 text-sm text-st-down"
        >
          Could not load journal updates: {readMutationError(runsQuery.error)}
        </div>
      ) : (runsQuery.data?.length ?? 0) === 0 ? (
        <div className="rounded-lg border border-line bg-bg-elev px-4 py-6 text-sm text-ink-3">
          No sync runs have been recorded yet.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {runsQuery.data?.map((run) => (
            <RunLog key={run.id} run={run} />
          ))}
        </div>
      )}
    </div>
  )
}
